import { useCallback, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import api from '../api/api'
import StoreBadges from '../components/StoreBadges'
import useDocumentMeta from '../hooks/useDocumentMeta'
import { SUPPORT_EMAIL, SUPPORT_PHONE } from '../config/support'
import './LandingPage.css'

const IMG = '/assets/images/landing'

// The home-screen services of the customer app (yum-express-app
// HomeScreen.tsx SERVICES): same names, same colours. The web has its own
// pages for food, groceries and offers; the other services are in the app.
const SERVICES = [
  { key: 'food', label: 'Food', icon: 'ph-fork-knife', bg: '#16A34A', to: '/restaurants' },
  { key: 'grocery', label: 'Fresh & Grocery', icon: 'ph-basket', bg: '#0EA5E9', to: '/groceries' },
  { key: 'pharmacy', label: 'Pharmacy', icon: 'ph-pill', bg: '#10B981', app: true },
  { key: 'electronics', label: 'Electronics', icon: 'ph-device-mobile', bg: '#2563EB', app: true },
  { key: 'fashion', label: 'Fashion', icon: 'ph-t-shirt', bg: '#FA5200', app: true },
  { key: 'butcher', label: 'Butcher', icon: 'ph-cow', bg: '#BE123C', app: true },
  { key: 'gas', label: 'Gas', icon: 'ph-flame', bg: '#F97316', app: true },
  { key: 'parcel', label: 'Send a parcel', icon: 'ph-package', bg: '#FB7185', app: true },
  { key: 'events', label: 'Events', icon: 'ph-ticket', bg: '#9333EA', app: true },
  { key: 'offers', label: 'Offers', icon: 'ph-seal-percent', bg: '#EC4899', to: '/shop' }
]

const TRACK_STEPS = [
  { key: 'confirmed', label: 'Order confirmed', note: 'The restaurant or shop accepts it', icon: 'ph-check-circle' },
  { key: 'preparing', label: 'Preparing', note: 'Your order is being made or packed', icon: 'ph-cooking-pot' },
  { key: 'assigned', label: 'Rider assigned', note: 'A rider heads to collect it', icon: 'ph-identification-badge' },
  { key: 'on_the_way', label: 'On the way', note: 'Follow the rider on the map', icon: 'ph-moped' },
  { key: 'delivered', label: 'Delivered', note: 'At your door, office or hotel', icon: 'ph-house-line' }
]

const NAV_LINKS = [
  { href: '#top', label: 'Home' },
  { href: '#food', label: 'Food' },
  { href: '#groceries', label: 'Groceries' },
  { href: '#how-it-works', label: 'How it works' },
  { href: '#download', label: 'Download' }
]

// Load a section's data only once it is about to scroll into view, so the
// page paints without waiting on the network.
function useNearViewport(ref) {
  const [near, setNear] = useState(false)
  useEffect(() => {
    const el = ref.current
    if (!el || near || typeof window === 'undefined' || !('IntersectionObserver' in window)) return undefined
    const io = new IntersectionObserver(
      entries => {
        if (entries.some(e => e.isIntersecting)) {
          setNear(true)
          io.disconnect()
        }
      },
      { rootMargin: '300px 0px' }
    )
    io.observe(el)
    return () => io.disconnect()
  }, [ref, near])
  return near
}

// Sections ease in as they arrive. Content is visible without JavaScript,
// without IntersectionObserver, and for anyone who prefers reduced motion.
function useReveal(rootRef) {
  useEffect(() => {
    const root = rootRef.current
    if (!root || typeof window === 'undefined' || !('IntersectionObserver' in window)) return undefined
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined
    root.classList.add('lp--reveal')
    const io = new IntersectionObserver(
      entries =>
        entries.forEach(e => {
          if (e.isIntersecting) {
            e.target.classList.add('is-in')
            io.unobserve(e.target)
          }
        }),
      { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
    )
    root.querySelectorAll('[data-reveal]').forEach(el => io.observe(el))
    return () => io.disconnect()
  }, [rootRef])
}

function useScrolled(offset = 12) {
  const [scrolled, setScrolled] = useState(false)
  useEffect(() => {
    let raf = 0
    const onScroll = () => {
      if (raf) return
      raf = requestAnimationFrame(() => {
        raf = 0
        setScrolled(window.scrollY > offset)
      })
    }
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => {
      window.removeEventListener('scroll', onScroll)
      if (raf) cancelAnimationFrame(raf)
    }
  }, [offset])
  return scrolled
}

/** A real customer-app screen in a phone frame. */
const AppScreen = ({ name, alt, className = '', eager = false }) => (
  <div className={`lp-device ${className}`}>
    <picture>
      <source type="image/webp" srcSet={`${IMG}/${name}-320.webp 320w, ${IMG}/${name}-640.webp 640w`} sizes="(min-width: 960px) 300px, 62vw" />
      <img src={`${IMG}/${name}-640.jpg`} alt={alt} width="320" height="709" loading={eager ? 'eager' : 'lazy'} decoding="async" />
    </picture>
  </div>
)

const Photo = ({ name, alt, className = '' }) => (
  <picture className={`lp-photo ${className}`}>
    <source type="image/webp" srcSet={`${IMG}/${name}-440.webp`} />
    <img src={`${IMG}/${name}-440.jpg`} alt={alt} width="440" height="440" loading="lazy" decoding="async" />
  </picture>
)

const formatEnd = iso => {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return null
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
}

// Live offers only: the server returns promotions running now; anything whose
// end has passed since is dropped here too. Nothing is invented when there
// are none — the section then explains what offers are.
export const liveOffers = (promotions, now = Date.now()) =>
  (Array.isArray(promotions) ? promotions : [])
    .filter(p => p && p.name && p.is_active !== false)
    .filter(p => !p.end_date || new Date(p.end_date).getTime() > now)
    .filter(p => !p.start_date || new Date(p.start_date).getTime() <= now)
    .slice(0, 3)

const OffersStrip = () => {
  const ref = useRef(null)
  const near = useNearViewport(ref)
  const [offers, setOffers] = useState([])
  useEffect(() => {
    if (!near) return undefined
    let cancelled = false
    api
      .get('/promotions/home/', { suppressErrorToast: true })
      .then(({ data }) => {
        if (!cancelled) setOffers(liveOffers(data?.active_promotions))
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [near])

  return (
    <div ref={ref} className="lp-offers" aria-live="polite">
      {offers.length > 0 ? (
        <>
          <p className="lp-offers__label">Running now</p>
          <ul className="lp-offers__grid">
            {offers.map(o => (
              <li key={o.id} className="lp-offer lp-offer--live">
                <span className="lp-offer__tag">{o.badge_text || 'Offer'}</span>
                {o.discount_display && <p className="lp-offer__value">{o.discount_display}</p>}
                <p className="lp-offer__name">{o.name}</p>
                {o.end_date && formatEnd(o.end_date) && <p className="lp-offer__note">Ends {formatEnd(o.end_date)}</p>}
              </li>
            ))}
          </ul>
        </>
      ) : (
        <ul className="lp-offers__grid">
          <li className="lp-offer">
            <span className="lp-offer__icon" aria-hidden="true">
              <i className="ph-fill ph-storefront" />
            </span>
            <p className="lp-offer__name">Offers from participating vendors</p>
            <p className="lp-offer__note">Restaurants and shops run their own deals on the dishes and products they choose.</p>
          </li>
          <li className="lp-offer">
            <span className="lp-offer__icon" aria-hidden="true">
              <i className="ph-fill ph-lightning" />
            </span>
            <p className="lp-offer__name">Flash sales</p>
            <p className="lp-offer__note">Short-time deals, with the time left shown on the product.</p>
          </li>
          <li className="lp-offer">
            <span className="lp-offer__icon" aria-hidden="true">
              <i className="ph-fill ph-receipt" />
            </span>
            <p className="lp-offer__name">What you see is what you pay</p>
            <p className="lp-offer__note">An offer price on a product is the price charged at checkout.</p>
          </li>
        </ul>
      )}
    </div>
  )
}

const LandingPage = () => {
  useDocumentMeta({
    title: 'Yum Express — Food, Groceries & Delivery in Zanzibar',
    description: 'Order food, groceries and more with Yum Express. Discover local restaurants and shops, place your order and track your delivery in Zanzibar.',
    path: '/',
    robots: 'index, follow'
  })
  const rootRef = useRef(null)
  useReveal(rootRef)
  const scrolled = useScrolled()
  const [menuOpen, setMenuOpen] = useState(false)
  const menuButtonRef = useRef(null)

  const closeMenu = useCallback(() => setMenuOpen(false), [])
  useEffect(() => {
    if (!menuOpen) return undefined
    const onKey = e => {
      if (e.key === 'Escape') {
        setMenuOpen(false)
        menuButtonRef.current?.focus()
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [menuOpen])

  return (
    <div className="lp" ref={rootRef} id="top">
      <a href="#lp-main" className="lp-skip">
        Skip to content
      </a>

      {/* 1. NAVIGATION */}
      <header className={`lp-nav${scrolled || menuOpen ? ' is-solid' : ''}`}>
        <div className="lp-wrap lp-nav__inner">
          <Link to="/" className="lp-nav__logo" aria-label="Yum Express home">
            <picture>
              <source srcSet="/assets/images/logo/yum-express-logo.webp" type="image/webp" />
              <img src="/assets/images/logo/yum-express-logo.png" alt="Yum Express" width="170" height="24" />
            </picture>
          </Link>
          <nav className="lp-nav__links" aria-label="Main">
            {NAV_LINKS.map(l => (
              <a key={l.href} href={l.href}>
                {l.label}
              </a>
            ))}
          </nav>
          <div className="lp-nav__actions">
            <Link to="/account-login" className="lp-nav__signin">
              Sign in
            </Link>
            <Link to="/shop" className="lp-btn lp-btn--primary lp-btn--sm lp-nav__cta">
              Order now
            </Link>
            <button ref={menuButtonRef} type="button" className="lp-nav__menu" aria-expanded={menuOpen} aria-controls="lp-mobile-menu" onClick={() => setMenuOpen(o => !o)}>
              <i className={`ph-bold ${menuOpen ? 'ph-x' : 'ph-list'}`} aria-hidden="true" />
              <span className="visually-hidden">{menuOpen ? 'Close menu' : 'Open menu'}</span>
            </button>
          </div>
        </div>
        <div id="lp-mobile-menu" className="lp-menu" hidden={!menuOpen}>
          <nav aria-label="Main">
            {NAV_LINKS.map(l => (
              <a key={l.href} href={l.href} onClick={closeMenu}>
                {l.label}
              </a>
            ))}
            <Link to="/account-login" onClick={closeMenu}>
              Sign in
            </Link>
          </nav>
          <Link to="/shop" className="lp-btn lp-btn--primary lp-menu__cta" onClick={closeMenu}>
            Order now
          </Link>
        </div>
      </header>

      <main id="lp-main">
        {/* 2. HERO */}
        <section className="lp-hero" aria-labelledby="lp-hero-title">
          <div className="lp-wrap lp-hero__grid">
            <div className="lp-hero__copy">
              <p className="lp-eyebrow">
                <i className="ph-fill ph-map-pin" aria-hidden="true" /> Yum Express · Zanzibar
              </p>
              <h1 id="lp-hero-title" className="lp-hero__title">
                <span className="visually-hidden">Yum Express: </span>
                Food. Groceries. <span className="lp-accent">Delivered.</span>
              </h1>
              <p className="lp-hero__sub">Order from restaurants, shops and grocery stores around you — and track your delivery from order to doorstep.</p>
              <div className="lp-hero__ctas">
                <Link to="/shop" className="lp-btn lp-btn--primary lp-btn--lg">
                  Order now <i className="ph-bold ph-arrow-right" aria-hidden="true" />
                </Link>
                <a href="#download" className="lp-btn lp-btn--ghost lp-btn--lg">
                  <i className="ph-bold ph-device-mobile" aria-hidden="true" /> Download the app
                </a>
              </div>
              <StoreBadges size="sm" className="lp-hero__badges" />
            </div>

            <div className="lp-hero__visual">
              <picture className="lp-hero__photo">
                <source type="image/webp" srcSet={`${IMG}/hero-person-480.webp 480w, ${IMG}/hero-person-880.webp 880w`} sizes="(min-width: 960px) 560px, calc(100vw - 32px)" />
                <img
                  src={`${IMG}/hero-person-880.jpg`}
                  alt="A Yum Express customer in Zanzibar points to the Yum Express app on his phone, showing Food, Fresh & Grocery, Pharmacy and more"
                  width="880"
                  height="864"
                  fetchpriority="high"
                  decoding="async"
                />
              </picture>
              <div className="lp-hero__chip" aria-hidden="true">
                <span className="lp-hero__chip-icon">
                  <i className="ph-fill ph-navigation-arrow" />
                </span>
                <span>
                  <strong>Live tracking</strong>
                  <small>From order to doorstep</small>
                </span>
              </div>
            </div>
          </div>

          {/* 3. CATEGORY STRIP */}
          <div className="lp-wrap">
            <ul className="lp-strip" aria-label="What you can order">
              <li>
                <span className="lp-strip__icon lp-strip__icon--food" aria-hidden="true">
                  <i className="ph-fill ph-fork-knife" />
                </span>
                <span>
                  <strong>Food</strong>
                  <small>Restaurants near you</small>
                </span>
              </li>
              <li>
                <span className="lp-strip__icon lp-strip__icon--grocery" aria-hidden="true">
                  <i className="ph-fill ph-basket" />
                </span>
                <span>
                  <strong>Groceries</strong>
                  <small>Daily essentials</small>
                </span>
              </li>
              <li>
                <span className="lp-strip__icon lp-strip__icon--shops" aria-hidden="true">
                  <i className="ph-fill ph-storefront" />
                </span>
                <span>
                  <strong>Local shops</strong>
                  <small>More choices</small>
                </span>
              </li>
              <li>
                <span className="lp-strip__icon lp-strip__icon--delivery" aria-hidden="true">
                  <i className="ph-fill ph-moped" />
                </span>
                <span>
                  <strong>Delivery</strong>
                  <small>Tracked live</small>
                </span>
              </li>
            </ul>
          </div>
        </section>

        {/* 4. HOW IT WORKS */}
        <section id="how-it-works" className="lp-section" aria-labelledby="lp-how-title">
          <div className="lp-wrap">
            <div className="lp-head lp-head--center" data-reveal>
              <p className="lp-kicker">How it works</p>
              <h2 id="lp-how-title" className="lp-h2">
                Three steps to your door
              </h2>
            </div>
            <ol className="lp-steps">
              <li className="lp-step" data-reveal>
                <span className="lp-step__num">01</span>
                <h3>Choose what you need</h3>
                <p>Browse restaurants, groceries and local shops, or search for exactly what you want.</p>
              </li>
              <li className="lp-step" data-reveal>
                <span className="lp-step__num">02</span>
                <h3>Place your order</h3>
                <p>Choose where to deliver and pay with mobile money, card or cash on delivery.</p>
              </li>
              <li className="lp-step" data-reveal>
                <span className="lp-step__num">03</span>
                <h3>Track your delivery</h3>
                <p>Follow each step, then your rider on the map, until it reaches you.</p>
              </li>
            </ol>
          </div>
        </section>

        {/* 5. FOOD */}
        <section id="food" className="lp-section lp-section--dark" aria-labelledby="lp-food-title">
          <div className="lp-wrap lp-split">
            <div className="lp-copy" data-reveal>
              <p className="lp-kicker lp-kicker--light">Food</p>
              <h2 id="lp-food-title" className="lp-h2 lp-h2--light">
                Your favourite food, <span className="lp-accent">delivered.</span>
              </h2>
              <p className="lp-lead lp-lead--light">Discover restaurants and local favourites around you. See who's open, browse full menus with prices, and order in a few taps.</p>
              <Link to="/restaurants" className="lp-btn lp-btn--primary lp-btn--lg">
                Explore food <i className="ph-bold ph-arrow-right" aria-hidden="true" />
              </Link>
            </div>
            <div className="lp-stage lp-stage--food" data-reveal>
              <Photo name="photo-food" alt="A plate of grilled chicken, prawns and rice" className="lp-stage__photo" />
              <AppScreen name="app-restaurants" alt="Yum Express app: the Restaurants screen with recommendations and nearby restaurants" className="lp-stage__device" />
            </div>
          </div>
        </section>

        {/* 6. GROCERIES */}
        <section id="groceries" className="lp-section lp-section--warm" aria-labelledby="lp-groc-title">
          <div className="lp-wrap lp-split lp-split--flip">
            <div className="lp-stage lp-stage--groc" data-reveal>
              <Photo name="photo-groceries" alt="A paper bag of fresh vegetables, fruit and milk" className="lp-stage__photo" />
              <AppScreen name="app-groceries" alt="Yum Express app: the Yumi Groceries screen with categories and products" className="lp-stage__device" />
            </div>
            <div className="lp-copy" data-reveal>
              <p className="lp-kicker">Groceries</p>
              <h2 id="lp-groc-title" className="lp-h2">
                Fresh groceries, <span className="lp-accent">right to your door.</span>
              </h2>
              <p className="lp-lead">Do the weekly shop, or grab the one thing you forgot, from local grocery sellers.</p>
              <ul className="lp-ticks">
                <li>Fresh produce</li>
                <li>Household essentials</li>
                <li>Everyday shopping, by category or search</li>
                <li>Local grocery sellers</li>
              </ul>
              <Link to="/groceries" className="lp-btn lp-btn--primary lp-btn--lg">
                Shop groceries <i className="ph-bold ph-arrow-right" aria-hidden="true" />
              </Link>
            </div>
          </div>
        </section>

        {/* 7. LIVE DELIVERY TRACKING */}
        <section id="tracking" className="lp-section" aria-labelledby="lp-track-title">
          <div className="lp-wrap lp-split">
            <div className="lp-copy" data-reveal>
              <p className="lp-kicker">Live delivery tracking</p>
              <h2 id="lp-track-title" className="lp-h2">
                Know where your order is.
              </h2>
              <p className="lp-lead">See every step as it happens. Once a rider has your order, follow them on the map all the way to you.</p>
              <ol className="lp-track">
                {TRACK_STEPS.map((s, i) => (
                  <li key={s.key} className={`lp-track__step${i < 3 ? ' is-done' : ''}${i === 3 ? ' is-now' : ''}`}>
                    <span className="lp-track__icon" aria-hidden="true">
                      <i className={`ph-fill ${s.icon}`} />
                    </span>
                    <span className="lp-track__text">
                      <strong>{s.label}</strong>
                      <small>{s.note}</small>
                    </span>
                  </li>
                ))}
              </ol>
            </div>
            <div className="lp-stage lp-stage--track" data-reveal aria-hidden="true">
              <div className="lp-map">
                <svg viewBox="0 0 400 300" preserveAspectRatio="xMidYMid slice">
                  <rect width="400" height="300" fill="#eef2ea" />
                  <path d="M0 210 C 90 200, 120 120, 210 120 S 330 180, 400 90" stroke="#fff" strokeWidth="22" fill="none" />
                  <path d="M80 0 L 130 300 M260 0 L 300 300 M0 60 L 400 40" stroke="#fff" strokeWidth="12" fill="none" />
                  <path className="lp-map__route" d="M52 208 C 110 196, 140 124, 210 124 S 320 172, 352 96" fill="none" />
                </svg>
                <span className="lp-map__pin lp-map__pin--shop">
                  <i className="ph-fill ph-storefront" />
                </span>
                <span className="lp-map__pin lp-map__pin--rider">
                  <i className="ph-fill ph-moped" />
                </span>
                <span className="lp-map__pin lp-map__pin--home">
                  <i className="ph-fill ph-house" />
                </span>
              </div>
              <Photo name="photo-rider" alt="" className="lp-stage__rider" />
              <div className="lp-status">
                <span className="lp-status__badge">On the way</span>
                <p>Your rider is heading to you</p>
                <div className="lp-status__bar">
                  <span />
                  <span />
                  <span />
                  <span className="is-now" />
                  <span className="is-next" />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 8. VENDOR OFFERS */}
        <section id="offers" className="lp-section lp-section--warm" aria-labelledby="lp-offers-title">
          <div className="lp-wrap">
            <div className="lp-head" data-reveal>
              <p className="lp-kicker">Offers & deals</p>
              <h2 id="lp-offers-title" className="lp-h2">
                More ways to save.
              </h2>
              <p className="lp-lead">Discover offers from participating restaurants and shops. They're applied automatically, and the offer price is the price you pay.</p>
            </div>
            <OffersStrip />
            <Link to="/shop" className="lp-btn lp-btn--ghost">
              See today's offers <i className="ph-bold ph-arrow-right" aria-hidden="true" />
            </Link>
          </div>
        </section>

        {/* 9. PRODUCT REQUEST */}
        <section className="lp-section" aria-labelledby="lp-request-title">
          <div className="lp-wrap">
            <div className="lp-request" data-reveal>
              <div className="lp-request__copy">
                <p className="lp-kicker">Product requests</p>
                <h2 id="lp-request-title" className="lp-h2">
                  Can't find what you need?
                </h2>
                <p className="lp-lead">Tell us what you're looking for and request a product directly from Yum Express. Our team reviews every request.</p>
                <Link to="/search?request=1" className="lp-btn lp-btn--primary lp-btn--lg">
                  Request a product <i className="ph-bold ph-paper-plane-tilt" aria-hidden="true" />
                </Link>
              </div>
              <div className="lp-request__demo" aria-hidden="true">
                <div className="lp-request__search">
                  <i className="ph ph-magnifying-glass" /> Search for a product
                </div>
                <p className="lp-request__empty">No results for your search</p>
                <span className="lp-request__btn">
                  <i className="ph-bold ph-paper-plane-tilt" /> Request this product
                </span>
                <span className="lp-request__sent">
                  <i className="ph-fill ph-check-circle" /> Request sent
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* 10. MORE THAN FOOD */}
        <section className="lp-section lp-section--tint" aria-labelledby="lp-more-title">
          <div className="lp-wrap">
            <div className="lp-head lp-head--center" data-reveal>
              <p className="lp-kicker">More than food</p>
              <h2 id="lp-more-title" className="lp-h2">
                Everything you need, in one app.
              </h2>
              <p className="lp-lead">The same services you'll find on the Yum Express home screen. What's available depends on the shops near you.</p>
            </div>
            <ul className="lp-services" data-reveal>
              {SERVICES.map(s => {
                const inner = (
                  <>
                    <span className="lp-service__tile" style={{ background: s.bg }} aria-hidden="true">
                      <i className={`ph-bold ${s.icon}`} />
                    </span>
                    <span className="lp-service__label">{s.label}</span>
                    {s.app && <span className="lp-service__where">In the app</span>}
                  </>
                )
                return (
                  <li key={s.key}>
                    {s.to ? (
                      <Link to={s.to} className="lp-service">
                        {inner}
                      </Link>
                    ) : (
                      <a href="#download" className="lp-service" aria-label={`${s.label} — in the Yum Express app`}>
                        {inner}
                      </a>
                    )}
                  </li>
                )
              })}
            </ul>
          </div>
        </section>

        {/* 11. APP DOWNLOAD */}
        <section id="download" className="lp-section lp-download" aria-labelledby="lp-download-title">
          <div className="lp-wrap lp-download__inner">
            <div className="lp-download__copy" data-reveal>
              <h2 id="lp-download-title" className="lp-h2 lp-h2--light">
                Yum Express, wherever you go.
              </h2>
              <p className="lp-lead lp-lead--light">Get the Yum Express app and order from your phone.</p>
              <StoreBadges className="lp-download__badges" />
              <p className="lp-download__note">Available for iPhone and Android</p>
            </div>
            <div className="lp-download__phones" aria-hidden="true">
              <AppScreen name="app-restaurants" alt="" className="lp-download__device lp-download__device--back" />
              <AppScreen name="app-groceries" alt="" className="lp-download__device lp-download__device--front" />
            </div>
          </div>
        </section>
      </main>

      {/* 12. FOOTER */}
      <footer className="lp-footer">
        <div className="lp-wrap lp-footer__grid">
          <div className="lp-footer__brand">
            <picture>
              <source srcSet="/assets/images/logo/yum-express-logo.webp" type="image/webp" />
              <img src="/assets/images/logo/yum-express-logo.png" alt="Yum Express" width="150" height="21" loading="lazy" />
            </picture>
            <p>
              <i className="ph-fill ph-map-pin" aria-hidden="true" /> Zanzibar
            </p>
            <StoreBadges size="sm" />
          </div>
          <nav aria-label="Product">
            <h3>Product</h3>
            <ul>
              <li>
                <Link to="/restaurants">Food</Link>
              </li>
              <li>
                <Link to="/groceries">Groceries</Link>
              </li>
              <li>
                <Link to="/shop">Offers</Link>
              </li>
              <li>
                <Link to="/orders">Track order</Link>
              </li>
            </ul>
          </nav>
          <nav aria-label="Company">
            <h3>Company</h3>
            <ul>
              <li>
                <Link to="/contact">Contact</Link>
              </li>
              <li>
                <Link to="/contact?subject=Selling%20on%20Yum%20Express">Sell on Yum Express</Link>
              </li>
            </ul>
          </nav>
          <nav aria-label="Support">
            <h3>Support</h3>
            <ul>
              <li>
                <Link to="/contact">Help</Link>
              </li>
              <li>
                <a href={`mailto:${SUPPORT_EMAIL}`}>Contact support</a>
              </li>
              <li>
                <Link to="/terms">Terms</Link>
              </li>
              <li>
                <Link to="/privacy">Privacy</Link>
              </li>
            </ul>
          </nav>
        </div>
        <div className="lp-wrap lp-footer__base">
          <p>© {new Date().getFullYear()} Yum Express. All rights reserved.</p>
          <p>
            <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a> · <a href={`tel:${SUPPORT_PHONE.replace(/\s/g, '')}`}>{SUPPORT_PHONE}</a>
          </p>
        </div>
      </footer>
    </div>
  )
}

export default LandingPage
